import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { last } from 'lodash';

import { PageHeading } from 'web-ui-components/lib/typography/headings';
import { AccountPaymentTable } from 'web-ui-components/lib/finance/accounts';
import { Alert } from 'web-ui-components/lib/molecules/nbas';
import { StandardBody } from 'web-ui-components/lib/typography/body';
import { HighlightTile } from 'web-ui-components/lib/molecules/tile';

import {
	getPaymentListForTable,
	getShowAddPaymentButton,
	getInvalidPaymentIds,
	getSomePaymentRatesAreIndicative,
	isAuthMaxTransactionLimitError,
	getIFTPayments,
} from '../../../states/ift/selectors';
import PageNavigation from '../../../sharedComponents/PageNavigation/PageNavigation';
import { hasApiCallInProgress } from '../../../states/api/selectors';
import withContent from '../../../containers/withContent';
import iftPaymentContent, { contentPropTypes } from './iftPayments.content';
import AddMoreButton from '../../../sharedComponents/AddMoreButton';
import { goToPage, pages } from '../../../states/page/actions';
import UnSavedWarningModal from '../../../sharedComponents/UnSavedWarningModal/UnSavedWarningModal';
import { iftDeletePayment, iftSelectPayment } from '../../../states/ift/actions';
import { parseString } from '../../../helpers/contentParser';
import { resetPaymentAuthorisation } from '../../../states/payments/actions';
// import { IFTPaymentLimitsModal } from './IFTPaymentsLimitsModal';
import PaymentLimitsModal from '../../../fasterPayments/components/Payments/PaymentLimitsModal';
import { setSelectedFromAccount } from '../../../states/payeeSelector/actions';

const mapState = (state) => ({
	paymentList: getPaymentListForTable(state),
	hasApiCallInProgress: hasApiCallInProgress(state),
	showAddPaymentButton: getShowAddPaymentButton(state),
	invalidPaymentIds: getInvalidPaymentIds(state),
	somePaymentRatesAreIndicative: getSomePaymentRatesAreIndicative(state),
	iftPayments: getIFTPayments(state),
});

const mapDispatch = (dispatch) => ({
	goToPayeeSelector: () => dispatch(goToPage(pages.IFTPayeeSelector)),
	deletePayment: (id) => dispatch(iftDeletePayment(id)),
	editPayment: (id) => {
		dispatch(iftSelectPayment(id, true));
		dispatch(goToPage(pages.IFTPaymentDetails));
	},
	resetPaymentAuthorisation: (id) =>
		dispatch(
			resetPaymentAuthorisation({
				id,
			})
		),
	setSelectedFromAccount: (fromAccount) => dispatch(setSelectedFromAccount(fromAccount)),
});

class IFTPaymentList extends Component {
	constructor(props) {
		super(props);

		this.handleContinue = this.handleContinue.bind(this);
		this.handleAddMore = this.handleAddMore.bind(this);
		this.handleShowDeleteModal = this.handleShowDeleteModal.bind(this);
		this.handleDeletePayment = this.handleDeletePayment.bind(this);
		this.handleEditPayment = this.handleEditPayment.bind(this);

		this.state = {
			deleteModalOpen: false,
			showValidation: false,
		};
	}
	componentDidMount() {
		const { handleDisplayFullwidth = () => {} } = this.props;
		handleDisplayFullwidth();
	}

	handleContinue() {
		const { handleContinue, invalidPaymentIds } = this.props;
		if (invalidPaymentIds.length > 0) {
			this.setState({ showValidation: true });
			return null;
		}

		return handleContinue();
	}

	handleAddMore() {
		const { goToPayeeSelector, iftPayments, setSelectedFromAccount } = this.props;
		const { fromAccount } = last(iftPayments);
		setSelectedFromAccount(fromAccount);
		return goToPayeeSelector();
	}

	handleDeletePayment() {
		const { deletePayment } = this.props;
		deletePayment(this.state.paymentToDeleteId);
		this.setState({ deleteModalOpen: false });
	}

	handleShowDeleteModal(id) {
		this.setState({ deleteModalOpen: true, paymentToDeleteId: id });
	}

	handleEditPayment(id) {
		const { iftPayments, resetPaymentAuthorisation, editPayment } = this.props;
		const payment = iftPayments.find((payment) => payment.id === id);
		if (isAuthMaxTransactionLimitError(payment.authorisation)) resetPaymentAuthorisation(id);
		editPayment(id);
	}

	render() {
		const {
			paymentList,
			hasApiCallInProgress,
			content,
			showAddPaymentButton,
			invalidPaymentIds,
			somePaymentRatesAreIndicative,
		} = this.props;
		const { listHeader, fromLabel, toLabel, refLabel, dateLabel, amtLabel, paymentListInvalidMsg, indicativeRateMsg } =
			content;

		const { deleteModalOpen, showValidation } = this.state;

		const headerFields = [
			{ type: 'account', label: fromLabel },
			{ type: 'account', label: toLabel },
			{ type: 'currency', label: amtLabel },
			{ type: 'iban', label: refLabel },
			{ type: 'date', label: dateLabel },
		];

		const showValidationMsg = !!invalidPaymentIds.length && showValidation;

		return (
			<div>
				<PageHeading data-test-id="ift-payment-list">{listHeader}</PageHeading>

				<div className="c-o-list-payment__card c-o-card c-o-card--muted c-o-card--bordered">
					{showValidationMsg && (
						<p>
							<Alert type="warning">{paymentListInvalidMsg}</Alert>
						</p>
					)}
					<AccountPaymentTable
						headerData={headerFields}
						tableData={paymentList}
						editOnClick={(id) => this.handleEditPayment(id)}
						deleteOnClick={(id) => this.handleShowDeleteModal(id)}
						rowsWithError={showValidation ? invalidPaymentIds : []}
					/>

					{showAddPaymentButton && (
						<AddMoreButton onClick={this.handleAddMore} isDisabled={false} title={content.addButton} />
					)}

					{somePaymentRatesAreIndicative && (
						<HighlightTile type="info">
							<StandardBody isFlush>{parseString(indicativeRateMsg)}</StandardBody>
						</HighlightTile>
					)}
				</div>
				<PageNavigation
					showLoading={hasApiCallInProgress}
					handleContinue={this.handleContinue}
					showContinue
					enableContinue={!hasApiCallInProgress}
				/>

				<UnSavedWarningModal
					handleClose={() => {
						this.setState({ deleteModalOpen: false });
					}}
					isModalOpen={deleteModalOpen}
					handleBack={this.handleDeletePayment}
					contentKey="deleteIFTPaymentContent"
				/>
				{/* <IFTPaymentLimitsModal /> */}
				<PaymentLimitsModal />
			</div>
		);
	}
}

IFTPaymentList.propTypes = {
	paymentList: PropTypes.arrayOf(PropTypes.shape([])),
	hasApiCallInProgress: PropTypes.bool,
	handleContinue: PropTypes.func,
	goToPayeeSelector: PropTypes.func,
	content: PropTypes.shape(contentPropTypes).isRequired,
	showAddPaymentButton: PropTypes.bool,
	deletePayment: PropTypes.func,
	editPayment: PropTypes.func,
	handleDisplayFullwidth: PropTypes.func,
	invalidPaymentIds: PropTypes.arrayOf(PropTypes.string),
	somePaymentRatesAreIndicative: PropTypes.bool,
	iftPayments: PropTypes.arrayOf(PropTypes.shape([])),
	resetPaymentAuthorisation: PropTypes.func,
	setSelectedFromAccount: PropTypes.func.isRequired,
};

export default compose(connect(mapState, mapDispatch), withContent({ content: iftPaymentContent }))(IFTPaymentList);
